/**
 * Site map view state per browser history entry.
 *
 * Pure: parses whatever was stored (it may be stale, from an older build, or tampered with)
 * and keeps a short most-recent-first history, so storage stays small while back and
 * forward through recent visits still restore each one.
 */
import type { MapViewport, SiteMapSelection, SiteMapViewState } from '../models/siteMapModels';

export interface ViewStateRecord {
  readonly key: string;
  readonly state: SiteMapViewState;
}

export type ViewStateHistory = readonly ViewStateRecord[];

/** Enough for back and forward through a few site visits */
export const VIEW_STATE_HISTORY_LIMIT = 10;

export const EMPTY_VIEW_STATE: SiteMapViewState = {
  viewport: null,
  searchQuery: '',
  selection: null,
};

type UnknownRecord = Readonly<Record<string, unknown>>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteBetween(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
}

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function parseViewport(raw: unknown): MapViewport | null {
  if (!isRecord(raw) || !Array.isArray(raw.center) || raw.center.length !== 2) return null;
  const [lng, lat] = raw.center as readonly unknown[];
  if (!isFiniteBetween(lng, -180, 180) || !isFiniteBetween(lat, -90, 90)) return null;
  if (typeof raw.zoom !== 'number' || !Number.isFinite(raw.zoom)) return null;
  return { center: [lng, lat], zoom: raw.zoom };
}

function parseSelection(raw: unknown): SiteMapSelection {
  if (!isRecord(raw)) return null;
  if (raw.kind === 'site' && typeof raw.siteId === 'string') {
    return { kind: 'site', siteId: raw.siteId };
  }
  if (raw.kind === 'cluster' && isStringArray(raw.siteIds)) {
    return { kind: 'cluster', siteIds: [...raw.siteIds] };
  }
  return null;
}

function parseViewState(raw: unknown): SiteMapViewState {
  if (!isRecord(raw)) return EMPTY_VIEW_STATE;
  return {
    viewport: parseViewport(raw.viewport),
    searchQuery: typeof raw.searchQuery === 'string' ? raw.searchQuery : EMPTY_VIEW_STATE.searchQuery,
    selection: parseSelection(raw.selection),
  };
}

/** Never throws; anything unreadable is dropped. */
export function parseViewStateHistory(raw: unknown): ViewStateHistory {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item: unknown) =>
    isRecord(item) && typeof item.key === 'string'
      ? [{ key: item.key, state: parseViewState(item.state) }]
      : []
  );
}

export function findViewState(history: ViewStateHistory, key: string): SiteMapViewState | null {
  return history.find((record) => record.key === key)?.state ?? null;
}

/** Merges the patch into the key's state (or the empty state) and moves it to the front. */
export function withViewState(
  history: ViewStateHistory,
  key: string,
  patch: Partial<SiteMapViewState>,
  limit: number = VIEW_STATE_HISTORY_LIMIT
): ViewStateHistory {
  const state = { ...(findViewState(history, key) ?? EMPTY_VIEW_STATE), ...patch };
  const others = history.filter((record) => record.key !== key);
  return [{ key, state }, ...others].slice(0, limit);
}
